import { action, computed, observable, toJS } from 'mobx';
import i18n from 'i18next';
import { v4 as uuid } from 'uuid';

class CartStore {
  constructor(root) {
    this.root = root;
  }

  @observable cartData = {
    companyId: '',
    storeCode: '',
    receiveMethodCode: undefined,
    menus: [],
  }; // 장바구니 데이터
  @observable isDialogOpen = false; // 장바구니 비우기 다이얼로그 오픈 여부
  @observable couponDialog = {
    isOpen: false,
    uuid: '',
    itemCode: '',
    couponID: '',
  };
  @observable couponData = undefined;

  // 장바구니 메뉴 총 개수
  @computed
  get totalCnt() {
    return this.cartData.menus.map(menu => menu.qty).reduce((accumulator, qty) => accumulator + qty, 0);
  }

  /**
   * 세션 스토리지에서 장바구니 데이터 조회하여 세팅
   */
  @action
  setCartDataFromSessionStorage() {
    if (this.cartData.menus.length === 0) {
      // 세션 스토리지에서 장바구니 데이터 조회
      const sessionCartData = JSON.parse(sessionStorage.getItem('cartData'));

      if (sessionCartData && sessionCartData.menus && sessionCartData.menus.length > 0) {
        const menu = sessionCartData.menus.find(menu => menu.topingGroups);
        if (menu) {
          sessionCartData.menus = [];
          sessionStorage.setItem('cartData', JSON.stringify(sessionCartData));
        }
      }

      // 현재 매장의 장바구니 데이터인 경우에만 세팅
      if (
        sessionCartData &&
        sessionCartData.companyId === this.root.commonStore.companyId &&
        sessionCartData.storeCode === this.root.commonStore.storeCode
      ) {
        this.cartData = sessionCartData;
      } else {
        this.cartData.companyId = this.root.commonStore.companyId;
        this.cartData.storeCode = this.root.commonStore.storeCode;
      }
    }
  }

  /**
   * 장바구니에 메뉴 추가
   */
  @action
  addCartMenu() {
    const menuStore = this.root.menuStore;

    // 옵션 유효성 검사
    let inValidOptionNames;
    if (menuStore.selectedMenu.toppingGroups && menuStore.selectedMenu.toppingGroups.length > 0) {
      // 미선택 필수 토핑 그룹 이름 조회
      inValidOptionNames = menuStore.selectedMenu.toppingGroups
        .filter(
          toppingGroup =>
            toppingGroup.minCount > 0 &&
            toppingGroup.minCount > toppingGroup.toppings.filter(topping => topping.isChecked).length,
        )
        .map(toppingGroup => toppingGroup.name)
        .reduce(
          (accumulator, currentValue) => (accumulator === '' ? currentValue : accumulator + ', ' + currentValue),
          '',
        );
    }

    if (menuStore.selectedMenu.menuType === 2) {
      // 세트메뉴 그룹 중 메뉴가 1개인 세트메뉴는 자동 선택
      // menuStore.selectedMenu.setMenuGroups
      //   .filter(setMenuGroup => setMenuGroup.setMenus.length === 1)
      //   .forEach(setMenuGroup => {
      //     setMenuGroup.setMenus.forEach(setMenu => (setMenu.isChecked = true));
      //   });

      // 미선택 세트메뉴 그룹 이름 조회
      inValidOptionNames = menuStore.selectedMenu.setMenuGroups
        .filter(setMenuGroup => setMenuGroup.setMenus.filter(setMenu => setMenu.isChecked).length === 0)
        .map(setMenuGroup => setMenuGroup.name)
        .reduce(
          (accumulator, currentValue) => (accumulator === '' ? currentValue : accumulator + ', ' + currentValue),
          '',
        );
    }

    if (inValidOptionNames) {
      this.root.commonStore.setSnackbar(true, `${i18n.t('menu.error.optionRequired')} (${inValidOptionNames})`);
      return;
    }

    // 메뉴 데이터 복사
    const cartMenu = toJS(menuStore.selectedMenu, { recurseEverything: true });

    // 고유값 세팅
    cartMenu.uuid = uuid();

    // 수량 세팅
    cartMenu.qty = menuStore.qty;

    // 메뉴 데이터에서 선택한 토핑만 필터링
    cartMenu.toppingGroups = cartMenu.toppingGroups
      .filter(toppingGroup => toppingGroup.toppings.filter(topping => topping.isChecked).length > 0)
      .map(toppingGroup => {
        toppingGroup.toppings = toppingGroup.toppings.filter(topping => topping.isChecked);
        return toppingGroup;
      });

    // 메뉴 데이터에서 선택한 세트메뉴만 필터링
    cartMenu.setMenuGroups = cartMenu.setMenuGroups
      .filter(setMenuGroup => setMenuGroup.setMenus.filter(setMenu => setMenu.isChecked).length > 0)
      .map(setMenuGroup => {
        setMenuGroup.setMenus = setMenuGroup.setMenus.filter(setMenu => setMenu.isChecked);
        return setMenuGroup;
      });

    // 필터링 된 메뉴 데이터 장바구니에 추가
    this.cartData.menus.push(cartMenu);

    // 장바구니 저장
    this.setSessionCartData();

    // 장바구니 추가 완료 메시지
    this.root.commonStore.setSnackbar(true, i18n.t('cart.message.added'));
    return true;
  }

  /**
   * 장바구니 비우기 다이얼로그 오픈 여부 설정
   * @param {boolean} isOpen 장바구니 비우기 다이얼로그 오픈 여부
   */
  @action
  setDialogOpen(isOpen) {
    this.isDialogOpen = isOpen;
  }

  /**
   * 장바구니 비우기
   */
  @action
  clearCart() {
    this.cartData.menus = [];
    this.setSessionCartData();
  }

  /**
   * 장바구니에서 특정 메뉴 삭제
   * @param {number} idx 메뉴 인덱스
   */
  @action
  removeItemInCart(idx) {
    this.cartData.menus.splice(idx, 1);
    this.setSessionCartData();
  }

  /**
   * 장바구니 특정 메뉴의 수량 변경
   * @param {number} index 장바구니 메뉴 인덱스
   * @param {number} qty 수량
   */
  @action
  setCartMenuQty(index, qty) {
    const cartMenu = this.cartData.menus[index];

    if (cartMenu.couponData) {
      this.root.commonStore.setSnackbar(true, i18n.t('cart.error.couponMenuQuantity'));
      return;
    }

    cartMenu.qty = qty;
    this.setSessionCartData();
  }

  /**
   * 수령방법 코드 설정
   * @param {number} code 수령방법 ENUM 코드
   */
  @action
  setReceiveMethod(code) {
    this.cartData.receiveMethodCode = code;
    this.setSessionCartData();
  }

  /**
   * 쿠폰 모달 설정
   * @param {boolean} isOpen 쿠폰 모달 오픈 여부
   * @param {object} cartMenu 장바구니 데이터
   */
  @action
  setCouponDialog(isOpen, cartMenu = {}) {
    this.couponData = undefined;
    this.couponDialog = {
      isOpen,
      uuid: cartMenu.uuid || '',
      itemCode: cartMenu.itemCode || '',
      couponID: cartMenu.couponID || '',
    };
  }

  /**
   * 쿠폰 ID 세팅
   * @param {string} value 입력한 쿠폰 ID
   */
  @action
  setCouponId(value) {
    this.couponDialog.couponID = value;
  }

  /**
   * 쿠폰 데이터 세팅
   * @param {object} value 조회한 쿠폰 데이터
   */
  @action
  setCouponData(value) {
    this.couponData = value;
  }

  /** 장바구니 메뉴에 쿠폰 적용 */
  @action
  applyCouponToCartMenu() {
    const foundCartMenu = this.cartData.menus.find(menu => menu.uuid === this.couponDialog.uuid);

    if (foundCartMenu.qty > 1) {
      const cartMenuIndex = this.cartData.menus.findIndex(menu => menu === foundCartMenu);
      foundCartMenu.qty -= 1;

      const separatedCartMenu = toJS(foundCartMenu, { recurseEverything: true });
      separatedCartMenu.uuid = uuid();
      separatedCartMenu.qty = 1;
      separatedCartMenu.couponData = toJS(this.couponData);
      this.cartData.menus.splice(cartMenuIndex, 0, separatedCartMenu);
    } else {
      foundCartMenu.couponData = this.couponData;
    }

    // 장바구니 저장
    this.setSessionCartData();
  }

  @action
  deleteCouponToCartMenu(uuid) {
    // 메뉴 조회
    const foundCartMenu = this.cartData.menus.find(menu => menu.uuid === uuid);

    // 쿠폰 데이터 삭제
    delete foundCartMenu.couponData;

    // 장바구니 저장
    this.setSessionCartData();
  }

  /**
   * 장바구니 데이터 세션 스토리지에 저장
   */
  setSessionCartData() {
    sessionStorage.setItem('cartData', JSON.stringify(this.cartData));
  }

  /**
   * 특정 메뉴의 토핑 이름 '/'로 구분하여 조회
   * @param {number}} idx 메뉴 인덱스
   */
  getToppingNames(idx) {
    if (this.cartData.menus[idx] && this.cartData.menus[idx].toppingGroups) {
      return this.cartData.menus[idx].toppingGroups
        .flatMap(toppingGroup => toppingGroup.toppings)
        .map(topping => `${topping.name} ${topping.qty > 1 ? `x${topping.qty}` : ''}`)
        .join(' / ');
    } else {
      return '';
    }
  }

  /**
   * 특정 메뉴의 세트메뉴 이름 '/'로 구분하여 조회
   * @param {number}} idx 메뉴 인덱스
   */
  getSetMenuNames(idx) {
    if (this.cartData.menus[idx] && this.cartData.menus[idx].setMenuGroups) {
      return this.cartData.menus[idx].setMenuGroups
        .flatMap(setMenuGroup => setMenuGroup.setMenus)
        .map(setMenu => setMenu.name)
        .join(' / ');
    } else {
      return '';
    }
  }
}

export default CartStore;
